import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, todayStr } from "@/lib/constants";
import GlobalSearch from "@/components/GlobalSearch";
import TaskDialog from "@/components/TaskDialog";
import WorkloadRebalance from "@/components/WorkloadRebalance";
import { WorkloadTrends } from "@/components/WorkloadTrends";
import DashboardTaskRow from "@/components/dashboard/DashboardTaskRow";
import { STATUS_PILL, GROUP_ORDER, ACTION_CARDS, FOLDER_TABS } from "@/lib/dashboardTheme";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, FileText, ArrowRightLeft } from "lucide-react";

const soonStr = (d) => { const t = new Date(); t.setDate(t.getDate() + d); return t.toISOString().slice(0, 10); };
const isOver = (t) => t.end_date && t.end_date < todayStr() && t.status !== "completed";
const isSoon = (t) => t.end_date && t.status !== "completed" && !isOver(t) && t.end_date <= soonStr(3);

const lsGet = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("localStorage set failed", k, e); } };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "admin" || user?.can_manage;

  const [folder, setFolder] = useState(() => lsGet("twp_dash_folder", "all_current"));
  const [mine, setMine] = useState(false);
  const [board, setBoard] = useState(null);
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [personFilter, setPersonFilter] = useState(null);
  const [collapsed, setCollapsed] = useState(() => lsGet("twp_dash_collapsed", {}));
  const [showCompleted, setShowCompleted] = useState(false);
  const [limits, setLimits] = useState({});
  const [dialog, setDialog] = useState({ open: false, task: null });
  const [rebalance, setRebalance] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(Date.now());

  const loadBoard = useCallback(async () => {
    const r = await api.get(`/dashboard/board`, { params: { folder, mine } });
    setBoard(r.data);
    setRefreshedAt(Date.now());
  }, [folder, mine]);

  const loadSide = useCallback(async () => {
    const [d, u] = await Promise.all([api.get("/dashboard"), api.get("/users")]);
    setData(d.data); setMembers(u.data);
  }, []);

  useEffect(() => { loadSide(); }, [loadSide]);
  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => {
    const iv = setInterval(() => { loadBoard(); }, 60000);
    return () => clearInterval(iv);
  }, [loadBoard]);

  const pickFolder = (f) => { setFolder(f); lsSet("twp_dash_folder", f); setActiveCard(null); };
  const toggleCollapse = (g) => setCollapsed((c) => { const n = { ...c, [g]: !c[g] }; lsSet("twp_dash_collapsed", n); return n; });
  const onChanged = (id, patch) => setBoard((b) => b ? { ...b, tasks: b.tasks.map((t) => t.task_id === id ? { ...t, ...patch } : t) } : b);

  const cardMatch = (t, key) => key === "overdue" ? isOver(t) : key === "due_soon" ? isSoon(t) : t.status === key;

  const filtered = useMemo(() => {
    let ts = board?.tasks || [];
    if (personFilter) ts = ts.filter((t) => (t.assigned_to || []).includes(personFilter));
    if (activeCard) ts = ts.filter((t) => cardMatch(t, activeCard));
    return ts;
  }, [board, activeCard, personFilter]);

  // Assignees present in the current folder's tasks (drives the filter dropdown).
  const assigneeOptions = useMemo(() => {
    const seen = new Map();
    (board?.tasks || []).forEach((t) => {
      (t.assigned_to || []).forEach((uid, i) => {
        if (!seen.has(uid)) seen.set(uid, (t.assignee_names || [])[i] || uid);
      });
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [board]);

  // Group: Overdue (virtual pinned) then GROUP_ORDER statuses; completed optional.
  const groups = useMemo(() => {
    const out = [];
    const overdue = filtered.filter(isOver).sort((a, b) => (a.end_date || "").localeCompare(b.end_date || ""));
    if (overdue.length) out.push({ key: "overdue", label: "Overdue", pill: STATUS_PILL.overdue, tasks: overdue });
    const rest = filtered.filter((t) => !isOver(t));
    GROUP_ORDER.forEach((g) => {
      const list = rest.filter((t) => t.status === g).sort((a, b) => (a.end_date || "9999").localeCompare(b.end_date || "9999"));
      if (list.length) out.push({ key: g, label: (STATUS_PILL[g] || {}).label || g, pill: STATUS_PILL[g] || STATUS_PILL.assigned, tasks: list });
    });
    if (showCompleted) {
      const done = filtered.filter((t) => t.status === "completed");
      if (done.length) out.push({ key: "completed", label: "Completed", pill: STATUS_PILL.completed, tasks: done });
    }
    return out;
  }, [filtered, showCompleted]);

  const secsAgo = Math.max(0, Math.round((Date.now() - refreshedAt) / 1000));
  const ac = board?.action_counts || {};

  return (
    <div className="min-h-screen" style={{ background: "var(--twp-bg)" }}>
      {/* Sticky header + tabs */}
      <div className="sticky top-0 z-20 border-b" style={{ background: "var(--twp-bg)", borderColor: "var(--twp-border)" }}>
        <div className="px-4 lg:px-8 pt-5 pb-3 flex items-center gap-4 flex-wrap">
          <h1 className="font-heading text-2xl font-black tracking-tight" style={{ color: "var(--twp-text)" }}>Studio Dashboard</h1>
          <div className="flex-1 min-w-[180px] max-w-md"><GlobalSearch /></div>
          <div className="flex rounded-full p-0.5 text-xs font-semibold" style={{ background: "#ECEAE5" }} data-testid="scope-toggle">
            {[["Studio", false], ["My work", true]].map(([l, v]) => (
              <button key={l} onClick={() => setMine(v)} className="px-3 py-1.5 rounded-full transition-colors"
                style={mine === v ? { background: "var(--twp-charcoal)", color: "#fff" } : { color: "var(--twp-text-2)" }}
                data-testid={`scope-${v ? "mine" : "studio"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="px-4 lg:px-8 flex gap-1 overflow-x-auto no-scrollbar" data-testid="folder-tabs">
          {FOLDER_TABS.map((f) => {
            const on = folder === f.key;
            return (
              <button key={f.key} onClick={() => pickFolder(f.key)} data-testid={`folder-tab-${f.key}`}
                className="px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors shrink-0"
                style={on ? { borderColor: "var(--twp-accent)", color: "var(--twp-text)" } : { borderColor: "transparent", color: "var(--twp-text-2)" }}>
                {f.label} <span className="text-xs" style={{ color: "var(--twp-muted)" }}>{board?.folder_counts?.[f.key] ?? ""}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 lg:px-8 py-5 grid grid-cols-1 min-[1400px]:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-6">
        {/* MAIN */}
        <div className="min-w-0">
          {/* Action cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="action-cards">
            {ACTION_CARDS.map((c) => {
              const on = activeCard === c.key;
              return (
                <button key={c.key} onClick={() => setActiveCard(on ? null : c.key)} data-testid={`action-card-${c.key}`}
                  className="text-left rounded-xl border p-3 transition-colors"
                  style={{ background: on ? c.bg : "var(--twp-card)", borderColor: "var(--twp-border)", borderLeft: `4px solid ${c.color}`, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.04em]" style={{ color: "var(--twp-muted)" }}>{c.label}</p>
                  <p className="font-heading text-2xl font-black mt-1" style={{ color: c.color }}>{ac[c.key] ?? 0}</p>
                </button>
              );
            })}
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-3 mb-2 text-xs" style={{ color: "var(--twp-muted)" }}>
            <button onClick={loadBoard} className="inline-flex items-center gap-1 hover:text-[var(--twp-text)]" data-testid="board-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refreshed {secsAgo < 5 ? "just now" : `${secsAgo}s ago`}
            </button>
            <Select value={personFilter || "all"} onValueChange={(v) => setPersonFilter(v === "all" ? null : v)}>
              <SelectTrigger className="h-7 w-auto min-w-[150px] gap-1.5 rounded-full border-[var(--twp-border)] bg-[var(--twp-card)] px-3 text-xs font-medium" data-testid="assignee-filter">
                <SelectValue placeholder="All assignees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="assignee-filter-all">All assignees</SelectItem>
                {assigneeOptions.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`assignee-filter-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} data-testid="show-completed" /> Show completed
            </label>
          </div>

          {/* Column headers */}
          <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_140px_90px_92px_120px_120px_56px_120px] gap-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.04em]" style={{ color: "var(--twp-muted)" }}>
            <span>Name</span><span>Status</span><span>Priority</span><span>Due</span><span>Assignee</span><span>Created by</span><span>Chat</span><span>Folder</span>
          </div>

          <div className="rounded-xl border overflow-hidden" style={{ background: "var(--twp-card)", borderColor: "var(--twp-border)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }} data-testid="task-board">
            {!board ? (
              <div className="p-10 flex justify-center"><div className="h-6 w-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>
            ) : groups.length === 0 ? (
              <p className="p-10 text-center text-sm" style={{ color: "var(--twp-muted)" }}>Nothing here — you&apos;re all caught up.</p>
            ) : groups.map((g) => {
              const isCol = collapsed[g.key];
              const lim = limits[g.key] || 15;
              const shown = g.tasks.slice(0, lim);
              return (
                <div key={g.key} data-testid={`group-${g.key}`}>
                  <button onClick={() => toggleCollapse(g.key)} className="w-full flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: "var(--twp-divider)" }}>
                    <span className="text-xs transition-transform" style={{ transform: isCol ? "rotate(-90deg)" : "none", color: "var(--twp-muted)" }}>▾</span>
                    <span className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide" style={{ background: g.pill.bg, color: g.pill.text }}>{g.label}</span>
                    <span className="text-xs font-semibold" style={{ color: "var(--twp-muted)" }}>{g.tasks.length}</span>
                  </button>
                  {!isCol && shown.map((t) => (
                    <DashboardTaskRow key={t.task_id} task={t} members={members} canManage={canManage}
                      onOpenTask={(tk) => setDialog({ open: true, task: { ...tk } })} onChanged={onChanged} />
                  ))}
                  {!isCol && g.tasks.length > lim && (
                    <button onClick={() => setLimits((l) => ({ ...l, [g.key]: lim + 15 }))} className="w-full py-2 text-xs font-semibold hover:bg-[var(--twp-row-hover)]" style={{ color: "var(--twp-accent)" }} data-testid={`show-more-${g.key}`}>
                      Show more ({g.tasks.length - lim})
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SIDE PANEL */}
        <div className="min-w-0 space-y-6">
          {data?.can_view_stats && (
            <div className="rounded-xl border p-1" style={{ background: "var(--twp-card)", borderColor: "var(--twp-border)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              <WorkloadTrends onPersonClick={(uid) => setPersonFilter(uid)} onRebalance={canManage ? (p) => setRebalance(p) : undefined} />
            </div>
          )}
          {data?.missing_docs_count > 0 && (
            <div className="rounded-xl border" style={{ background: "var(--twp-card)", borderColor: "var(--twp-border)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }} data-testid="missing-docs-side">
              <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--twp-divider)" }}>
                <FileText className="h-4 w-4" style={{ color: "#B42318" }} />
                <h3 className="font-heading font-bold text-sm">Missing pre-design docs</h3>
                <span className="ml-auto text-xs font-semibold rounded-full px-2 py-0.5" style={{ background: "#FDECEC", color: "#B42318" }}>{data.missing_docs_count}</span>
              </div>
              <div className="divide-y" style={{ borderColor: "var(--twp-divider)" }}>
                {data.missing_docs.slice(0, 5).map((m) => (
                  <button key={m.project_id} onClick={() => navigate(`/project/${m.project_id}`)} className="w-full text-left px-4 py-2.5 hover:bg-[var(--twp-row-hover)]" data-testid={`missing-doc-side-${m.project_id}`}>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate flex-1" style={{ color: "var(--twp-text)" }}>{m.name}</p>
                      <span className="text-[11px] font-semibold shrink-0" style={{ color: "#B42318" }}>{m.total - m.missing}/{m.total}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(m.missing_items || []).slice(0, 3).map((it) => (
                        <span key={it} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#F1F1EF", color: "var(--twp-text-2)" }}>{it}</span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => navigate("/all-tasks")} className="w-full py-2 text-xs font-semibold" style={{ color: "var(--twp-accent)" }}>View all</button>
            </div>
          )}
        </div>
      </div>

      <TaskDialog open={dialog.open} onOpenChange={(o) => { setDialog((d) => ({ ...d, open: o })); if (!o) { loadBoard(); } }}
        task={dialog.task} projectId={dialog.task?.project_id} projectName={dialog.task?.project_name}
        members={members} canManage={canManage} currentUser={user} onSaved={loadBoard} />
      <WorkloadRebalance person={rebalance} perPerson={{}} open={!!rebalance} onOpenChange={(o) => !o && setRebalance(null)} onDone={() => { loadBoard(); loadSide(); }} />
    </div>
  );
}
